/**
 * Sistema de coach del A0 (`.co-*` del bundle del lab, plan 34). Codifica la
 * REGLA DE PRESENCIA del avatar (docs/speak-plan/34-a0-design-brief.md §2):
 *  - `CoachStage size="lg"` → Nivel 1 protagonista (Umbral, Hito). Card grande
 *    con halo + waveform + badge EN + `co-say` (línea EN del coach + "de nuevo").
 *  - `CoachStage size="md"` → Nivel 1 acompañando (Pizarrón): frame solo, sin
 *    `co-say` (el Pizarrón pone su propia aside).
 *  - `CoachMini` → Nivel 2 locutor mínimo (Juego de escucha): avatar compacto +
 *    waveform, o sólo un ícono de audio ("oír").
 *
 * El avatar es un `<video>` de Hedra (lip-sync). Sin `videoSrc` (clip aún no
 * subido a Tigris / sin signer) cae al poster estático con idle breathing
 * (degradación cálida — siempre hay cara). En estático autoplaya muteado en
 * loop (presencia); con `driverRef` el motor de escena maneja el tiempo.
 *
 * El coach SÓLO "habla" cuando reproduce su clip con audio/lip-sync real; las
 * palabras de los ejercicios son audios aparte y el coach queda neutro mientras
 * tanto (coherencia decidida en el lab). El texto en pantalla matchea el clip.
 *
 * Portado de labs/a0-scene-lab/src/ui/CoachCard.tsx. Estilos en a0-lab.css.
 */
import { useEffect, useRef } from 'react';
import type { CSSProperties, Ref } from 'react';
import { bindMediaAudioFocus } from '@orborix/voice-client';
import { A0Retry } from './icons.js';

// Activación de usuario "sticky": una vez que el usuario tocó algo en la sesión, el
// browser permite reproducir CON audio sin un tap dedicado (la activación persiste
// por la vida del documento). Marcamos el primer gesto y lo usamos para desmutear el
// clip del coach al montar → saca el "de nuevo" obligatorio. Para LLEGAR a cualquier
// pantalla del coach (Hito) el usuario YA gesticuló, así que en la práctica suena solo.
let a0UserGestured = false;
if (typeof window !== 'undefined') {
  const mark = (): void => {
    a0UserGestured = true;
    window.removeEventListener('pointerdown', mark);
    window.removeEventListener('keydown', mark);
    window.removeEventListener('touchstart', mark);
  };
  window.addEventListener('pointerdown', mark);
  window.addEventListener('keydown', mark);
  window.addEventListener('touchstart', mark);
}

/** Assets de un momento del coach: poster (siempre) + clip Hedra (opcional). */
export interface A0CoachAssets {
  poster: string;
  /** Clip de Hedra con lip-sync (signed Tigris). Ausente → poster-only. */
  video?: string | undefined;
}

function Wave({ on, count = 4, sm }: { on?: boolean | undefined; count?: number; sm?: boolean }) {
  return (
    <span className={`co-wave ${on ? 'on' : ''} ${sm ? 'sm' : ''}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <i key={i} style={{ animationDelay: `${i * 0.1}s` }} />
      ))}
    </span>
  );
}

interface CoachStageProps {
  size: 'lg' | 'md';
  speaking?: boolean;
  statusText: string;
  accent?: string;
  assets: A0CoachAssets;
  say?: { en: string; onReplay?: () => void };
  /** Si está, el `<video>` es el driver del motor (no autoplaya). */
  driverRef?: Ref<HTMLVideoElement>;
  /**
   * Mantiene el clip VISIBLE (último frame congelado) aunque ya no esté "hablando".
   * Sin esto, al terminar el video el layer cae al poster con un fade que se ve raro
   * (el frame final salta a la foto estática). Con `hold` el avatar queda quieto en
   * su último frame; sólo se apaga el halo/onda de "hablando".
   */
  hold?: boolean;
  /**
   * El `<video>` no pudo cargar el clip (404 del asset firmado, red caída, códec).
   * El caller lo necesita para degradar A TIEMPO: sin esta señal el motor de escena
   * se quedaba esperando un `currentTime` que nunca avanzaba y recién cerraba por el
   * timer de seguridad — 14s de pantalla muerta. Ver `A0SceneStage`.
   */
  onVideoError?: () => void;
}

export function A0CoachStage({
  size,
  speaking,
  statusText,
  accent = 'var(--lime)',
  assets,
  say,
  driverRef,
  hold,
  onVideoError,
}: CoachStageProps) {
  const driven = Boolean(driverRef);
  const internalRef = useRef<HTMLVideoElement>(null);

  // Arranque del clip en modo NO-driven (Hito/Umbral): si hubo un gesto en la sesión
  // (siempre que se llega acá), reproducimos UNA vez CON audio (el coach "dice" su
  // línea). Si el browser igual lo bloquea (Safari estricto sin activación en-tarea),
  // caemos a muted+loop (presencia) y queda el botón "de nuevo" como respaldo. En
  // modo driven el padre (Pizarrón/escena) controla el play → no tocamos nada.
  useEffect(() => {
    if (driven) return;
    const el = internalRef.current;
    if (!el || !assets.video) return;
    if (a0UserGestured) {
      el.muted = false;
      el.loop = false;
      el.currentTime = 0;
      void el.play().catch(() => {
        el.muted = true;
        el.loop = true;
        void el.play().catch(() => {});
      });
    } else {
      el.muted = true;
      el.loop = true;
      void el.play().catch(() => {});
    }
  }, [driven, assets.video]);

  // El clip del coach habla: entra al mismo árbitro que el resto del audio (ver
  // `audioFocus`). Sólo el modo NO-driven — en driven el elemento es del padre,
  // que lo ata por su cuenta. El fallback de presencia (muted+loop) NO toma el
  // foco: `bindMediaAudioFocus` ignora los elementos muteados.
  useEffect(() => {
    if (driven) return;
    const el = internalRef.current;
    if (!el) return;
    return bindMediaAudioFocus(el, 'speak:a0-coach-clip');
  }, [driven, assets.video]);

  // "de nuevo" → re-reproduce el clip CON audio UNA vez (el tap es gesto explícito).
  const replay = (): void => {
    const el = internalRef.current;
    if (el) {
      el.muted = false;
      el.loop = false;
      el.currentTime = 0;
      void el.play().catch(() => {});
    }
    say?.onReplay?.();
  };

  return (
    <div
      className={`co-stage size-${size} ${speaking ? 'is-speaking' : ''}`}
      style={{ '--co-accent': accent } as CSSProperties}
    >
      <div className="co-frame">
        <span className="co-halo" aria-hidden="true" />
        <span className={`co-media fr-bust ${speaking || hold ? 'playing' : ''}`}>
          <img className="co-poster" src={assets.poster} alt="" aria-hidden="true" />
          {assets.video ? (
            // Reproducción manejada por el effect (no-driven) o el padre (driven);
            // sin atributos autoPlay/muted declarativos (el effect setea muted/loop
            // por ref para poder desmutear con el gesto previo de la sesión).
            <video
              className="co-vid"
              src={assets.video}
              ref={driven ? driverRef : internalRef}
              playsInline
              preload="auto"
              aria-hidden="true"
              onError={onVideoError}
            />
          ) : null}
        </span>
        <span className="co-grain" aria-hidden="true" />
        <span className="co-badge">EN</span>
        <span className="co-status">
          <Wave on={speaking} /> {statusText}
        </span>
      </div>
      {say ? (
        <div className="co-say">
          <div className="co-say-head">
            <span className="co-say-who">
              <Wave on={speaking} /> Coach
            </span>
            <button className="co-replay" aria-label="Escuchar de nuevo" onClick={replay}>
              <A0Retry size={13} /> de nuevo
            </button>
          </div>
          <div className="co-say-en">{say.en}</div>
        </div>
      ) : null}
    </div>
  );
}

interface CoachMiniProps {
  variant: 'avatar' | 'icon';
  on?: boolean;
  assets?: A0CoachAssets;
  onClick?: () => void;
}

export function A0CoachMini({ variant, on, assets, onClick }: CoachMiniProps) {
  if (variant === 'icon') {
    return (
      <button className={`co-mini icon ${on ? 'on' : ''}`} aria-label="Escuchar al coach" onClick={onClick}>
        <Wave on={on} count={5} />
        <span className="co-mini-lbl">oír</span>
      </button>
    );
  }
  return (
    <button className={`co-mini ${on ? 'on' : ''}`} aria-label="Escuchar al coach" onClick={onClick}>
      <span className="co-mini-av">
        <span className={`co-media fr-bust ${on ? 'playing' : ''}`}>
          {assets ? <img className="co-poster" src={assets.poster} alt="" aria-hidden="true" /> : null}
          {assets?.video ? (
            <video className="co-vid" src={assets.video} playsInline preload="auto" muted loop autoPlay aria-hidden="true" />
          ) : null}
        </span>
      </span>
      <span className="co-mini-txt">
        <span className="co-mini-lbl">Coach</span>
        <Wave on={on} count={5} sm />
      </span>
    </button>
  );
}
